import {
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable, Injector, NgZone } from '@angular/core';
import { ApiError } from '@univacity/shared/types';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, finalize, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../services';

@Injectable()
export class SessionInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<void | null>(null);
  private authService: AuthService;

  constructor(inj: Injector) {
    inject(NgZone).runOutsideAngular(() => {
      setTimeout(() => (this.authService = inj.get(AuthService)), 0);
    });
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(this.addCredential(request)).pipe(
      catchError((requestError: ApiError) => {
        if (
          requestError &&
          this.authService?.isLoggedIn &&
          [401].includes(requestError.statusCode || 0)
        ) {
          return this.handle401Error(request, next);
        }
        return throwError(() => requestError);
      })
    );
  }

  handle401Error(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    console.log('Handling 401');
    if (this.isRefreshing) {
      return this.refreshTokenSubject.pipe(
        take(1),
        switchMap(() => next.handle(this.addCredential(request)))
      );
    } else {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);
      return this.authService?.refreshOfflineToken().pipe(
        switchMap(() => {
          this.refreshTokenSubject.next();
          return next.handle(this.addCredential(request));
        }),
        finalize(() => {
          this.isRefreshing = false;
          console.log('Handling 401 Done');
        })
      );
    }
  }

  addCredential(request: HttpRequest<any>) {
    const r = request.clone({
      withCredentials:
        request.url === 'https://api.db-ip.com/v2/free/self' ? false : true,
      headers: new HttpHeaders({
        Authorization: `Bearer ${this.authService?.accessToken}`,
      }),
    });
    return r;
  }
}
